import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { Editor } from '@tinymce/tinymce-react';

import { fetcher } from '../utils';
import ReportEditor from './ReportEditor';

const Reports = ({ month, batchNumber, updateBatchNumber }) => {

    const editorRef = useRef(null);
    const { register, handleSubmit, watch, reset, setValue } = useForm({
        defaultValues: {
            subject: '【{公司名稱}】{員工姓名} {薪資單年月} 薪資單',
            content: ''
        }
    });
    const [ viewId, setViewId ] = useState(null);
    const [ editId, setEditId ] = useState(null);
    const [ loading, setLoading ] = useState(false);

    const { data: reports, isLoading, isValidating } = useSWR( `/api/wage-processor/reports?month=${month}&rnd=${batchNumber}`, fetcher, {
        revalidateOnReconnect: false, revalidateOnFocus: false, keepPreviousData: true
    });

    const { data: detaultMailContent, isLoading: detaultMailContentLoading } = useSWR('/api/setting/wage_mail_default_content', fetcher, {
        revalidateOnReconnect: false, revalidateOnFocus: false, keepPreviousData: true
    });

    useEffect(() => {
        if (detaultMailContent) {
            setValue('content', detaultMailContent);
            // editorRef.current.setContent(data)
        }
    }, [detaultMailContent])

    const formValues = watch();

    const editingReport = useMemo(() => {
        if (!editId || !reports) return null;
        return reports?.find(report => report.id === editId)?.report;
    }, [editId, reports])
    
    async function onWagedListSubmit(formData) {
        setLoading(true);
        
        try {
            const { data } = await axios.post('/api/wage-processor/send-reports', formData);
        }
        catch (e) {
            alert(`寄送失敗，請速速把這個畫面截給工程師\n\nname:\n${e.name}\n\nmesasage:\n${e.message}\n\ncode:\n${e.code}`);
            setLoading(false);
            return;
        }

        alert('寄送成功！');
        setLoading(false);
        updateBatchNumber();
        reset();
    }

    async function onReportDeleteClick(id) {
        if (!confirm('確定要刪除嗎？')) return;
        await axios.delete(`/api/wage-processor/report/${id}/delete`);
        updateBatchNumber();
    }

    function handleMailContentChange(value) {
        setValue('content', value)
    }

    return (
        <StyledReports loading={isLoading || isValidating || loading}>
            { (!editId) &&
                <ListAndSendBlock onSubmit={handleSubmit(onWagedListSubmit)}>
                    <table>
                        <thead>
                            <tr>
                                <th>寄送</th>
                                <th>員工姓名</th>
                                <th>實領薪資</th>
                                <th>加班費</th>
                                <th>請假扣薪</th>
                                {/* <th>標記</th> */}
                                <th>淨工時</th>
                                <th>產製時間</th>
                                <th>已寄送？</th>
                                <th>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            { reports?.map?.( (report, index) => {
                                return (
                                    <tr key={index}>
                                        <td>
                                            <input type="checkbox" id={`check-${report.id}`} {...register('wage_report_ids[]')} value={report.id} />
                                        </td>
                                        <td>
                                            <label htmlFor={`check-${report.id}`}>{ report.user.name }</label>
                                        </td>
                                        <td>{ (report.report?.sum || 0).toLocaleString() }</td>
                                        <td>{ (report.report?.salary.find(s => s.note === '加班費')?.amount ?? 0).toLocaleString() }</td>
                                        <td>{ (report.report?.debt.find(s => s.note === '請假扣薪')?.amount ?? 0).toLocaleString() }</td>
                                        
                                        {/* <td>{ report.status || '-' }</td> */}
                                        <td>{ 
                                            report.report.flexible_hour_duration_type?.replace?.('班', '') + 
                                            ( report.report.flexible_hour_duration_type ? '：' : '') + 
                                            report.report.flexible_hour_duration 
                                        }</td>
                                        <td>{ new Date(report.updated_at).toLocaleString('zh-TW') }</td>
                                        <td>{ report.last_send_record ? new Date(report.last_send_record.created_at).toLocaleString('zh-TW') : '-' }</td>
                                        <td className='buttons'>
                                            <button type="button" onClick={() => setViewId(report.id)}>檢視</button>
                                            <button type="button" onClick={() => setEditId(report.id)}>編輯</button>
                                            <button type="button" onClick={() => onReportDeleteClick(report.id)}>刪除</button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    
                    <div className={`send-tools ${!(formValues.wage_report_ids?.length) ? '-disabled': ''}`}>
                        
                        <label>信件標題</label>
                        <input type='text' {...register('subject')} />
                        
                        <label>信件內容</label>
                        { !detaultMailContentLoading &&
                            <Editor
                                tinymceScriptSrc='https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.1.2/tinymce.min.js'
                                onInit={(evt, editor) => editorRef.current = editor}
                                onEditorChange={handleMailContentChange}
                                initialValue={detaultMailContent}
                                init={{
                                    width: '100%',
                                    height: 200,
                                    resize: true,
                                    menubar: false,
                                    plugins: [
                                        'advlist autolink lists link image charmap print preview anchor',
                                        'searchreplace visualblocks fullscreen',
                                        'insertdatetime media table paste code help wordcount'
                                    ],
                                    toolbar: 'undo redo | formatselect | bold italic forecolor backcolor |  outdent indent | removeformat | code',
                                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                }}
                            />
                        }

                        <button className='btn btn-default' type="submit">寄送薪資條</button>
                    </div>
                    
                    <div className='hint'>
                        <h2>信件可可自動帶入內容的參數：</h2>
                        <ul>
                            <li>{`{公司名稱}`} - 如 "簡訊設計行銷有限公司"</li>
                            <li>{`{員工姓名}`} - 如 "王成祥"</li>
                            <li>{`{薪資單年月}`} - 如 "2023-01"</li>
                            <li>{`{薪資單年月繁體}`} - 如 "2023 年 01 月"</li>
                        </ul>
                    </div>
                </ListAndSendBlock>
            }

            { (editId && editingReport) && 
                <div>
                    <button type='button' onClick={() => setEditId(null)}>取消編輯</button>
                    <h2>編輯 { editingReport.user_name} { editingReport.name }</h2>
                    <ReportEditor reportId={editId} report={editingReport} 
                        onClose={() => setEditId(null)} 
                        onSubmit={() => updateBatchNumber(batchNumber + 1)}
                    />
                </div>
            }

            { viewId && 
                <ViewBlock>
                    <button type='button' onClick={() => setViewId(null)}>關閉</button>
                    <iframe src={`/wage-processor/report/${viewId}/view`} />
                </ViewBlock> 
            }
            
        </StyledReports>
    )
}

const StyledReports = styled.div`

    ${ p => p.loading && css`
        opacity: 0.5;
        pointer-events: none;
    `}
`

const ListAndSendBlock = styled.form`

    table {
        width: 100%;
    }

    th {
        padding: 8px 0;
    }

    label {
        cursor: pointer;
    }

    .buttons > button { 
        margin-right: 4px;
    }

    tbody > tr:hover > td {
        background: #fff;
    }

    .send-tools {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #ddd;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        &.-disabled {
            opacity: 0.5;
            pointer-events: none;
        }

        label {
            margin-top: 8px;
        }
        input, textarea {
            margin-top: 0px;
            width: 100%;
            border-radius: 0;
        }
        textarea {
            resize: vertical;
        }

        button {
            margin-top: 8px;
            align-self: flex-end;
        }
    }
`

const ViewBlock = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 10000;
    padding: 40px;

    iframe {
        width: 100%;
        height: 100%;
    }

    button {
        position: absolute;
        top: 0; right: 40px;
        width: 80px;
        height: 40px;
    }
`

export default Reports